import React, { useState, useCallback, useRef } from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import {
  Button,
  Container,
  Header,
  SpaceBetween,
} from "@amzn/awsui-components-react-v3";
import { EmployeeDto } from "@amzn/squad-front-end-shared-react-components/dist/components/popover/personpopover/get-employee-response.dto";
import TeamInformations from "./informations/TeamInformations";
import EditTeamForm from "./editTeam/EditTeamForm";
import { TeamDtoWithBreadcrumb } from "../layout/subheader/SubHeaderDto";
import useWorkflowAuthorizationCheck, {
  UseWorkflowAuthorizationHookResult,
} from "../common/authorization/workflowAuthorization";
import { MetricsState } from "../logging";
import useEditTeamValidate from "./editTeam/useEditTeamValidate";
import { EditTeamResult } from "./editTeam/EditTeamResult";
import teamFormSchema from "../common/schema/teamFormSchema";
import { AutoSuggestItem } from "../search/AutoSuggestResponse";
import { EditTeamUpdateDto } from "../common/model/edit-team-update.dto";
import bannerSlice, { DisplayBanner } from "../banner/bannerSlice";
import { LoadingState } from "../http";
import subheaderSlice, {
  GetSubheaderDetailFetchPayload,
} from "../layout/subheader/subheaderSlice";
import { TeamOwnerDetailsDto } from "../common/model/teamownerdetails.dto";

interface EditTeamComponentDispatchProps {
  setBanner: (banner: DisplayBanner | undefined) => void;
  getSubheaderDetailFetch: (payload: GetSubheaderDetailFetchPayload) => void;
}
interface EditTeamComponentProps {
  team: TeamDtoWithBreadcrumb;
  ownerDetails?: TeamOwnerDetailsDto;
  metrics: MetricsState;
  originalTeam: any;
}

const EditTeamComponent: React.FC<EditTeamComponentProps &
  EditTeamComponentDispatchProps> = ({
  team,
  ownerDetails,
  metrics,
  originalTeam,
  setBanner,
  getSubheaderDetailFetch,
}): JSX.Element => {
  const [showEditForm, setShowEditForm] = useState<boolean>(false);
  const [localChanges, setLocalChanges] = useState<Partial<EditTeamResult>>({});
  const [enableSaveButton, setEnableSaveButton] = useState<boolean>(false);

  const schema = teamFormSchema;
  const attributesToUpdate = useRef<EditTeamUpdateDto[]>([]);

  const {
    isAuthorized,
    loadingState: isAuthorizedLoadingState,
  }: UseWorkflowAuthorizationHookResult = useWorkflowAuthorizationCheck(
    team.teamId,
    metrics
  );

  const isCurrentUserAuthorized =
    isAuthorizedLoadingState === LoadingState.RESOLVED && isAuthorized;

  const detectChangesInForm = useCallback(
    (diff: Partial<EditTeamResult>) => {
      setLocalChanges(diff);
      attributesToUpdate.current = [];
      let isFormChanged = false;
      Object.entries(diff).forEach(([key, editedValue]) => {
        if (key === "owner") {
          let editedEmployeeId = "null";
          const editedOwner = editedValue as AutoSuggestItem;
          if (editedOwner && editedOwner.attributes) {
            editedEmployeeId = editedOwner.attributes.employeeId as string;
          }
          if (
            (originalTeam.owner ? originalTeam.owner.urlId : null) !==
            editedEmployeeId
          ) {
            isFormChanged = true;
            const update = {
              fieldName: "owner",
              value: { employeeId: editedEmployeeId },
            };

            attributesToUpdate.current.push(update);
          }
        } else if (originalTeam[key]) {
          if (originalTeam[key] !== editedValue) {
            isFormChanged = true;
            const update = {
              fieldName: key,
              value: editedValue ? (editedValue as string) : null,
            };
            attributesToUpdate.current.push(update);
          }
        } else if (editedValue && key !== "ownerInputValue") {
          isFormChanged = true;
          const update = {
            fieldName: key,
            value: editedValue ? (editedValue as string) : null,
          };
          attributesToUpdate.current.push(update);
        }
      });
      setEnableSaveButton(isFormChanged);
    },
    [originalTeam]
  );

  const onEditTeamPass = useCallback(() => {
    getSubheaderDetailFetch({
      id: team.teamId as string,
    });
    setBanner({
      type: "success",
      content: "Team information updated successfully.",
      dissmissible: true,
    });
    setShowEditForm(false);
    setEnableSaveButton(false);
  }, [setBanner, team.teamId, getSubheaderDetailFetch]);

  const onEditCancel = useCallback(() => {
    setShowEditForm(false);
    setEnableSaveButton(false);
  }, [setShowEditForm, setEnableSaveButton]);

  const { execute, loadingState, validationErrors } = useEditTeamValidate(
    schema,
    localChanges,
    metrics,
    onEditTeamPass,
    setBanner,
    originalTeam,
    attributesToUpdate.current,
    showEditForm
  );

  function editTeamHeader(): JSX.Element {
    if (showEditForm) {
      return (
        <SpaceBetween direction="horizontal" size="xs">
          <Button
            variant="primary"
            formAction="none"
            onClick={onEditCancel}
            disabled={loadingState === LoadingState.LOADING}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            formAction="none"
            onClick={execute}
            disabled={!enableSaveButton}
            loading={loadingState === LoadingState.LOADING}
          >
            Save changes
          </Button>
        </SpaceBetween>
      );
    }

    if (!isCurrentUserAuthorized || !team.reportsTo) {
      return <div />;
    }
    return (
      <Button
        onClick={() => {
          setShowEditForm(true);
          setBanner(undefined);
        }}
      >
        Edit
      </Button>
    );
  }

  const transformEmployeeDetails = (
    ownerDetails: TeamOwnerDetailsDto | undefined
  ): EmployeeDto | undefined => {
    return ownerDetails
      ? {
          employeeId: ownerDetails.employeeId,
          employeeLogin: ownerDetails.employeeLogin,
          employeeFullName: ownerDetails.employeeFullName,
          employeeStatus: ownerDetails.employeeStatus,
          jobTitleName: ownerDetails.jobTitleName,
          costCenterId: ownerDetails.costCenterId,
          costCenterName: ownerDetails.costCenterName,
          teamId: ownerDetails.teamId,
          teamName: ownerDetails.teamName,
        }
      : undefined;
  };

  function editTeamInfo(): JSX.Element {
    if (showEditForm) {
      return (
        <EditTeamForm
          originalTeam={originalTeam}
          metrics={metrics}
          ownerName={ownerDetails ? ownerDetails.employeeFullName : undefined}
          validationErrors={validationErrors}
          detectChangesInForm={detectChangesInForm}
        />
      );
    }
    return (
      <TeamInformations
        team={team}
        ownerName={ownerDetails ? ownerDetails.employeeFullName : undefined}
        metrics={metrics}
        employeeData={transformEmployeeDetails(ownerDetails)}
        isCurrentUserAuthorized={isCurrentUserAuthorized}
      />
    );
  }

  return (
    <Container
      header={
        <Header variant="h2" actions={editTeamHeader()}>
          Team information
        </Header>
      }
    >
      {editTeamInfo()}
    </Container>
  );
};

const mapDispatchToProperties = (
  dispatch: Dispatch
): EditTeamComponentDispatchProps => {
  return {
    setBanner: (banner: DisplayBanner | undefined) => {
      dispatch(bannerSlice.actions.setBanner(banner));
    },
    getSubheaderDetailFetch: (payload: GetSubheaderDetailFetchPayload) =>
      dispatch(subheaderSlice.actions.getSubheaderDetailFetch(payload)),
  };
};

export default connect(null, mapDispatchToProperties)(EditTeamComponent);
